<script setup lang="ts">
/**
 * 6-cell OTP input.
 *
 * Behaviour mirrors the design bundle's `OtpInput.jsx`:
 *   - paste-to-fill with non-digit characters stripped
 *   - keyboard ←/→ to navigate cells
 *   - Backspace empties the cell or jumps left when the cell is empty
 *   - autofocus first cell on mount
 *   - digits-only via `inputMode="numeric"` + `maxLength=1`
 *   - `error` flips the `pp-otp--err` modifier on the wrapper
 *
 * The `.pp-otp` / `.pp-otp__cell` classes live in
 * `src/design-system/shell.css` so the appearance matches every
 * other portal surface that renders OTPs.
 */
import { onMounted, ref, watch } from 'vue'

const model = defineModel<string>({ default: '' })

const props = withDefaults(
  defineProps<{
    length?: number
    disabled?: boolean
    error?: boolean
    autoFocus?: boolean
  }>(),
  {
    length: 6,
    disabled: false,
    error: false,
    autoFocus: true,
  },
)

// Array of input refs — one per cell. Bound via `:ref="setCellRef(i)"`
// in the template so we don't fight Vue's static template-ref typing.
const cells = ref<HTMLInputElement[]>([])

function setCellRef(i: number) {
  return (el: unknown) => {
    if (el instanceof HTMLInputElement) {
      cells.value[i] = el
    }
  }
}

onMounted(() => {
  if (props.autoFocus && cells.value[0]) {
    cells.value[0].focus()
  }
})

// Keep the cells array sized to `length` even after dynamic prop changes.
watch(
  () => props.length,
  (n) => {
    cells.value.length = n
  },
)

/** Read the i-th character of the model value, or '' if absent. */
function charAt(i: number): string {
  return model.value[i] ?? ''
}

/** Replace the i-th character of the model. Pads with spaces, then
 *  trims trailing whitespace so the value remains the shortest
 *  string that holds every entered digit. */
function setCharAt(i: number, c: string): void {
  const current = model.value || ''
  const padded = current.padEnd(props.length, ' ').split('')
  padded[i] = c || ' '
  const next = padded.join('').replace(/\s+$/, '')
  model.value = next
}

function focusCell(i: number): void {
  const el = cells.value[i]
  if (el) el.focus()
}

function onInput(e: Event, i: number): void {
  const target = e.target as HTMLInputElement
  const raw = target.value

  // Pasted content can land here when the browser dispatches an
  // input event for the multi-character paste before the paste
  // handler short-circuits it.
  if (raw.length > 1) {
    const digits = raw.replace(/\D/g, '').slice(0, props.length)
    model.value = digits
    const focusIdx = Math.min(digits.length, props.length - 1)
    // Reset the source cell to the canonical character so Vue's
    // input value matches the model after the upstream re-render.
    target.value = digits[i] ?? ''
    focusCell(focusIdx)
    return
  }

  const c = raw.replace(/\D/g, '')
  setCharAt(i, c)
  // Reset the cell value in case the user typed a non-digit — Vue
  // won't re-render the same `value` and the field would otherwise
  // keep the bad character.
  target.value = c
  if (c && i < props.length - 1) {
    focusCell(i + 1)
  }
}

function onKeydown(e: KeyboardEvent, i: number): void {
  if (e.key === 'Backspace') {
    if (!charAt(i) && i > 0) {
      focusCell(i - 1)
      e.preventDefault()
    }
    return
  }
  if (e.key === 'ArrowLeft' && i > 0) {
    focusCell(i - 1)
    e.preventDefault()
    return
  }
  if (e.key === 'ArrowRight' && i < props.length - 1) {
    focusCell(i + 1)
    e.preventDefault()
  }
}

function onPaste(e: ClipboardEvent): void {
  const text = e.clipboardData?.getData('text') ?? ''
  const digits = text.replace(/\D/g, '').slice(0, props.length)
  if (!digits) return
  model.value = digits
  const focusIdx = Math.min(digits.length, props.length - 1)
  focusCell(focusIdx)
  e.preventDefault()
}
</script>

<template>
  <div :class="['pp-otp', { 'pp-otp--err': error }]">
    <input
      v-for="i in length"
      :key="i - 1"
      :ref="setCellRef(i - 1)"
      class="pp-otp__cell mono"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      :maxlength="1"
      :value="charAt(i - 1)"
      :disabled="disabled"
      :aria-label="`Cifra ${i}`"
      @input="(e: Event) => onInput(e, i - 1)"
      @keydown="(e: KeyboardEvent) => onKeydown(e, i - 1)"
      @paste="onPaste"
    />
  </div>
</template>
